import { ObservationCode } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  DisplayCodeableConceptSchema,
  PaginatedBundleSchema,
  ValueTypeSchema,
} from '../domain/schema';
import type { WithId } from '../types';
import { BaseTimeSeriesRecordSchema } from '../vitals/schema';

export const ObservationStatus = [
  'registered',
  'preliminary',
  'final',
  'amended',
  'corrected',
  'cancelled',
  'entered-in-error',
  'unknown',
] as const;

export const ObservationStatusSchema = z.enum(ObservationStatus);

export const ObservationVitalMeasurementSchema =
  BaseTimeSeriesRecordSchema.extend({
    sensorId: z
      .string()
      .optional()
      .describe('The ID of the sensor that created the measurement'),
  });
export type ObservationVitalMeasurement = z.infer<
  typeof ObservationVitalMeasurementSchema
>;

export const ObservationSchema = z.object({
  code: z.nativeEnum(ObservationCode),
  measures: ObservationVitalMeasurementSchema.array(),
  status: ObservationStatusSchema,
  device: DisplayReferenceSchema,
  performers: DisplayReferenceSchema.array(),
  patient: DisplayReferenceSchema,
  dateTime: z.string(),
  admission: DisplayReferenceSchema.optional(),
});
export type Observation = z.infer<typeof ObservationSchema>;

export const ObservationGetResponseSchema = ObservationSchema.partial().extend({
  id: UUIDSchema,
});

export type ObservationGetResponse = z.infer<
  typeof ObservationGetResponseSchema
>;

const MeasuresSchema = BaseTimeSeriesRecordSchema.extend({
  sensorId: z
    .string()
    .optional()
    .describe('The ID of the sensor that created the measurement'),
});

export const CreateObservationSchema = z.object({
  code: z
    .nativeEnum(ObservationCode)
    .describe('The type of observation to be created'),
  subject: z.string().describe('The subject of the observation'),
  measures: z.array(MeasuresSchema).describe('Measures to be created').min(1),
});
export type CreateObservationRequest = z.infer<typeof CreateObservationSchema>;

export type CreateVitalsResponse = WithId<Partial<Observation>>;

export const ObservationReferenceRangeSchema = z.object({
  low: z.object({
    value: z.number(),
    unit: z.string(),
  }),
  high: z.object({
    value: z.number(),
    unit: z.string(),
  }),
});
export type ObservationReferenceRange = z.infer<
  typeof ObservationReferenceRangeSchema
>;

export const ObservationComponentSchema = z.object({
  code: DisplayCodeableConceptSchema,
  referenceRange: ObservationReferenceRangeSchema.optional(),
  interpretation: DisplayCodeableConceptSchema.optional(),
  value: ValueTypeSchema,
});
export type ObservationComponent = z.infer<typeof ObservationComponentSchema>;

/**
 * Pregnancy Observation
 */
export const ObservationPregnancySchema = z.object({
  lactation: z.boolean().describe('Is patient lactating'),
  isPregnant: z.boolean().describe('Is patient pregnant'),
  edd: z.string().optional().describe('Estimated date of delivery'),
});

export type ObservationPregnancy = z.infer<typeof ObservationPregnancySchema>;

export const ObservationPathologyTestResult = [
  ObservationCode.pathologyTestResult,
] as const;
export const ObservationPathologyTestResultSchema = z.enum(
  ObservationPathologyTestResult,
);

export const ObservationPathologySchema = z.object({
  code: ObservationPathologyTestResultSchema,
  status: ObservationStatusSchema,
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  basedOn: DisplayReferenceSchema,
  effectiveDateTime: z.string(),
  components: ObservationComponentSchema.array(),
});

export const ObservationResultSearchSchema = PaginationSchema.extend({
  code: z.nativeEnum(ObservationCode).optional(),
  status: ObservationStatusSchema.optional(),
  basedOn: z.string().optional(),
  subject: UUIDSchema.optional(),
});

export type ObservationResultSearch = z.infer<
  typeof ObservationResultSearchSchema
>;
export const ObservationReportComponentSchema = z.object({
  code: z.string().optional(),
  interpretation: z.string().optional(),
  referenceRange: z.string().optional(),
  valueQuantity: z.object({
    value: z.string().optional(),
    unit: z.string().optional(),
  }),
  valueString: z.string().optional(),
});

export const ObservationWithoutAdmissionSchema =
  ObservationPathologySchema.omit({
    admission: true,
  });
export const ObservationResultSchema = ObservationWithoutAdmissionSchema.extend(
  {
    components: ObservationReportComponentSchema.array(),
    id: UUIDSchema,
  },
);

export const ObservationResultsResponseSchema = PaginatedBundleSchema.extend({
  results: ObservationResultSchema.array().optional(),
});

export type ObservationResultsResponse = z.infer<
  typeof ObservationResultsResponseSchema
>;

export type ObservationResult = z.infer<typeof ObservationResultSchema>;
export type ObservationWithoutAdmission = z.infer<
  typeof ObservationWithoutAdmissionSchema
>;
export type ObservationPathologyComponent = z.infer<
  typeof ObservationReportComponentSchema
>;
